import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, AlertTriangle, XCircle, ArrowRight } from "lucide-react";

function parseUTC(dateStr) {
  if (!dateStr) return new Date();
  let s = String(dateStr);
  s = s.replace(/[Zz]$/, "").replace(/\+00:?00$/, "");
  s = s.replace(/(\.\d{3})\d*$/, "$1");
  if (!s.endsWith("Z")) s += "Z";
  return new Date(s);
}

const PHASE_NORMAL = "normal";
const PHASE_WARNING = "warning";
const PHASE_CRITICAL = "critical";
const PHASE_URGENT = "urgent";
const PHASE_EXPIRED = "expired";

function getPhase(secondsLeft) {
  if (secondsLeft <= 0) return PHASE_EXPIRED;
  if (secondsLeft <= 60) return PHASE_URGENT;
  if (secondsLeft <= 180) return PHASE_CRITICAL;
  if (secondsLeft <= 600) return PHASE_WARNING;
  return PHASE_NORMAL;
}

const phaseConfig = {
  [PHASE_NORMAL]: {
    bg: "bg-zinc-900",
    border: "border-zinc-800",
    text: "text-zinc-100",
    sub: "text-zinc-500",
    timer: "text-white",
    icon: Clock,
    iconColor: "text-zinc-500",
    btn: "bg-white text-[#111111] hover:bg-[#F5F5F5]",
  },
  [PHASE_WARNING]: {
    bg: "bg-amber-950",
    border: "border-amber-800/50",
    text: "text-amber-100",
    sub: "text-amber-300/80",
    timer: "text-amber-300",
    icon: AlertTriangle,
    iconColor: "text-amber-600",
    btn: "bg-amber-400 text-amber-950 hover:bg-amber-300",
  },
  [PHASE_CRITICAL]: {
    bg: "bg-red-950",
    border: "border-red-800/50",
    text: "text-red-100",
    sub: "text-red-300/80",
    timer: "text-red-300",
    icon: AlertTriangle,
    iconColor: "text-red-600",
    btn: "bg-red-500 text-white hover:bg-red-400",
  },
  [PHASE_URGENT]: {
    bg: "bg-red-950",
    border: "border-red-700/60",
    text: "text-red-50",
    sub: "text-red-200/80",
    timer: "text-red-200",
    icon: XCircle,
    iconColor: "text-red-600",
    btn: "bg-red-500 text-white hover:bg-red-400",
  },
  [PHASE_EXPIRED]: {
    bg: "bg-zinc-950",
    border: "border-zinc-800",
    text: "text-zinc-200",
    sub: "text-zinc-500",
    timer: "text-zinc-500",
    icon: XCircle,
    iconColor: "text-zinc-500",
    btn: "bg-blue-600 text-white hover:bg-blue-500",
  },
};

function formatTime(totalSeconds) {
  if (totalSeconds <= 0) return "00:00";
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const CountdownBanner = forwardRef(function CountdownBanner({ expiresAt, createdAt, onExpired }, ref) {
  const navigate = useNavigate();
  // Derive total TTL from backend timestamps (source of truth)
  const totalTtlSeconds = (expiresAt && createdAt)
    ? Math.max(1, Math.floor((new Date(expiresAt).getTime() - new Date(createdAt).getTime()) / 1000))
    : 30 * 60;
  const [secondsLeft, setSecondsLeft] = useState(() => {
    if (!expiresAt) return -1;
    return Math.max(0, Math.floor((parseUTC(expiresAt).getTime() - Date.now()) / 1000));
  });
  const [pulsing, setPulsing] = useState(false);

  // Expose pulse trigger to parent
  useImperativeHandle(ref, () => ({
    pulse() {
      setPulsing(true);
      setTimeout(() => setPulsing(false), 800);
    },
  }));

  // Tick every second
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((parseUTC(expiresAt).getTime() - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0 && onExpired) onExpired();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, onExpired]);

  if (!expiresAt || secondsLeft < 0) return null;

  const phase = getPhase(secondsLeft);
  const cfg = phaseConfig[phase];
  const Icon = cfg.icon;
  const isExpired = phase === PHASE_EXPIRED;

  const subText = isExpired
    ? null
    : phase === PHASE_CRITICAL
      ? "Last minutes for this address"
      : phase === PHASE_WARNING
        ? "Expiring soon"
        : "This address will expire";

  return (
    <motion.div
      className={`relative z-10 ${cfg.bg} ${cfg.border} border-b`}
      animate={pulsing ? { scale: [1, 1.005, 1] } : {}}
      transition={{ duration: 0.4 }}
      data-testid="countdown-banner"
      data-phase={phase}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
        {/* Left: Icon + Timer + Warning text */}
        <div className="flex items-center gap-3 min-w-0">
          <Icon size={18} className={cfg.iconColor} strokeWidth={2} />

          {isExpired ? (
            <div className="min-w-0">
              <p className={`text-sm font-semibold ${cfg.text}`} data-testid="countdown-expired-text">
                This address has expired
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-3 min-w-0">
              <span
                className={`font-mono text-lg font-bold tabular-nums tracking-wider ${cfg.timer} ${phase === PHASE_URGENT ? "countdown-urgent-glow" : ""}`}
                data-testid="countdown-timer"
              >
                {formatTime(secondsLeft)}
              </span>
              <span className={`text-xs ${cfg.sub} hidden sm:inline`}>remaining</span>
              {subText && (
                <>
                  <span className={`text-xs ${cfg.sub} hidden sm:inline`}>&middot;</span>
                  <span className={`text-xs font-medium ${cfg.sub} hidden sm:inline`} data-testid="countdown-sub-text">
                    {subText}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Right: CTA */}
        <button
          onClick={() => navigate("/pricing")}
          className={`shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${cfg.btn}`}
          data-testid="countdown-cta-button"
        >
          {isExpired ? "Upgrade to keep using email" : "Keep forever"}
          <ArrowRight size={14} />
        </button>
      </div>

      {/* Progress bar — visual time remaining */}
      {!isExpired && (
        <div className="h-0.5 w-full bg-black/20">
          <motion.div
            className={`h-full ${phase === PHASE_URGENT || phase === PHASE_CRITICAL ? "bg-red-500" : phase === PHASE_WARNING ? "bg-amber-400" : "bg-white/30"}`}
            style={{ width: `${Math.min((secondsLeft / totalTtlSeconds) * 100, 100)}%` }}
            transition={{ duration: 1, ease: "linear" }}
            data-testid="countdown-progress-bar"
          />
        </div>
      )}
    </motion.div>
  );
});

export default CountdownBanner;
